import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { showError, showSuccess } from "../../../utils/toastUtils";
import GlobalDeleteModal from "../../../components/partials/GlobalDeleteModal";
import StoreAdminAPI from "../../../api/StoreAdminApi";
import Pagination from "../../../components/partials/Pagination";
import TableShimmer from "../../../components/partials/TableShimmer";
import StoreAdminRow from "./StoreAdminRow";
import RowsPerPage from "../../../components/partials/RowsPerPage";

function List() {
  const [adminData, setAdminData] = useState([]); // State for store list
  const [pagination, setPagination] = useState({}); // State for pagination info
  const [allStoreAdmin, setAllStoreAdmin] = useState("0"); // State for pagination info
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableLoading, setTableLoading] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // search parameter and api callback
  const query = React.useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      search: searchParams.get("search") || null,
    }),
    [searchParams],
  );

  const fetchStoreAdminData = useCallback(async () => {
    setTableLoading(true);
    try {
      const res = await StoreAdminAPI.getList(query);

      if (res.code === 200) {
        const { admin, pagination } = res.data;
        setAdminData(admin || []);
        setPagination(pagination || {});
        setAllStoreAdmin(pagination.total || "0");
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchStoreAdminData();
  }, [fetchStoreAdminData]);

  const searchTimeout = useRef();
  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value);
    }, 1000);
  };

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);
      value ? params.set(key, value) : params.delete(key);

      if (value !== "key") {
        params.set("page", value);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );
  // end search parameter and api callback

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);

      const res = await StoreAdminAPI.deleteItem(deleteId);

      if (res.code === 200) {
        showSuccess(res.message);

        setAdminData((prev) => prev.filter((s) => s._id !== deleteId));
        setAllStoreAdmin((p) => p - 1);

        if (adminData.length === 1 && query.page > 1) {
          setSearchParams({ ...query, page: query.page - 1 });
        }

        document.querySelector("#delete-modal .btn-close").click();
      } else {
        showError(res.message);
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Manage Store Administrators</h4>
              <h6>
                Access the complete list of store admins, monitor activity, and
                update permissions easily.
              </h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/stores/admin/add-new" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Store Admin
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search By Name/Username"
                    defaultValue={query.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-12">
                <div className="countablepagination p-2">
                  <p>
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      allStoreAdmin,
                    )}{" "}
                    of {allStoreAdmin} results
                  </p>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>S. No.</th>
                    <th>Name</th>
                    <th>Contact Details</th>
                    <th>Store Details</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={query.limit} cols={7} />
                  ) : adminData.length > 0 ? (
                    adminData.map((item, index) => (
                      <StoreAdminRow
                        key={item._id}
                        item={item}
                        index={index}
                        page={query.page}
                        limit={query.limit}
                        onDelete={handleOpenDeleteModal}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center p-4 text-muted">
                        No Store Admin found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default List;
